<template>
  <section-page
    title="One-Click Calling"
    is-short
  >
    <p class="mb-150">One-click Calling will use the settings below to automatically call the contact's primary number
      when you hit the call button.
      <app-help-link page-uri="knowledge-base/how-to-guides/how-to-use-one-click-call/">Learn about one-click calling</app-help-link>
    </p>

    <div class="form_container">
      <form-field-select
        label="Use Campaign Number?"
        v-model="form.outbound_call_use_campaign"
      >
        <option :value="true">Yes</option>
        <option :value="false">No</option>
      </form-field-select>

      <form-field-select
        :label="form.outbound_call_use_campaign ? 'Fallback Number' : 'Call Number'"
        v-model="form.outbound_call_integration_number_id"
        help-text="Used if campaign number not used or set."
        :validation-errors="validationErrors.outbound_call_integration_number_id"
      >
        <option
          v-for="number in integrationNumbers"
          :key="number.id"
          :value="number.id"
        >{{ number.label }}
        </option>
      </form-field-select>

      <form-field-phone
        v-if="hasCallRailIntegrationFeature"
        label="Cell Phone Number for CallRail"
        v-model="form.outbound_call_cell_number"
        :validation-errors="validationErrors.outbound_call_cell_number"
        help-text="CallRail will connect with this cell phone for the call"
      ></form-field-phone>

      <div class="flex flex-end">
        <button-default :async-click="saveOneClickCallSavings">Save One-Click Call Settings</button-default>
      </div>
    </div>

    <hr>

    <div class="flex flex-between pb-100">
      <p>{{ oneClickMessage }}</p>

      <form-field-switch
        v-model="oneClick"
        label="Enable One-Click Calling"
        size=""
        :disabled="!canSetOneClick"
      ></form-field-switch>
    </div>
  </section-page>
</template>

<script>
export default {
  name: "ProfileOneClickSettings",
  data() {
    return {
      validationErrors: {},
      form: {
        id: null,
        outbound_call_use_campaign: this.$store.getters.authUser.outbound_call_use_campaign,
        outbound_call_integration_number_id: this.$store.getters.authUser.outbound_call_integration_number_id,
        outbound_call_cell_number: this.$store.getters.authUser.outbound_call_cell_number,
      },
      oneClickMessage: null,
    };
  },
  computed: {
    authUser() {
      return this.$store.getters.authUser;
    },
    hasCallRailIntegrationFeature() {
      return this.$store.getters.hasCallRailIntegration;
    },
    integrationNumbers() {
      return this.$store.state.market.integrationPhoneNumbers;
    },
    canSetOneClick() {
      // Disable if form is dirty
      if (
        this.authUser.outbound_call_use_campaign !== this.form.outbound_call_use_campaign ||
        this.authUser.outbound_call_integration_number_id !== this.form.outbound_call_integration_number_id ||
        this.authUser.outbound_call_cell_number !== this.form.outbound_call_cell_number
      ) {
        this.oneClickMessage = "Please save your changes before toggling one-click calling";
        return false;
      }

      // Disable if an integration number is not selected
      if (!this.authUser.outbound_call_integration_number_id) {
        this.oneClickMessage = "Please choose an integration number before enabling one-click calling";
        return false;
      }

      // disable if has callrail and no cell phone given
      if (this.hasCallRailIntegrationFeature && !this.authUser.outbound_call_cell_number) {
        this.oneClickMessage = "Please enter a cell number before enabling one-click calling";
        return false;
      }

      this.oneClickMessage = "";
      return true;
    },
    oneClick: {
      // TODO: pipeline-overhaul - Shawn: Need to make this so it's really reactive, because if you update one click it isn't reactive
      // Shawn has ideas on how to do this so that's why he needs to play with it
      get() {
        return this.authUser.outbound_call_one_click;
      },
      set(value) {
        const form = {
          id: this.authUser.id,
          outbound_call_one_click: value,
        };
        const toastMessage = value ? "One-click turned on" : "One-click turned off";

        this.$api.users.toggleOneClick(form, this.validationErrors)
          .then(() => {
            this.$buefy.toast.open({
              message: toastMessage,
              type: "is-success"
            });
          });
      }
    },
  },
  created() {
    const user = this.$store.getters.authUser;

    this.form.id = user.id;
    this.form.outbound_call_use_campaign = user.outbound_call_use_campaign;
    this.form.outbound_call_integration_number_id = user.outbound_call_integration_number_id;
    this.form.outbound_call_cell_number = user.outbound_call_cell_number;
  },
  methods: {
    saveOneClickCallSavings() {
      return this.$api.users.updateOutboundCallSettings(this.form, this.validationErrors)
        .then(() => {
          this.validationErrors = {}
          this.$buefy.toast.open({
            message: "One-click settings saved",
            type: "is-success"
          });
        })
        .catch((error) => {
          if (error.response.status === 422) this.validationErrors = error.response.data.errors
        });
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../../../styles/variables/variables";

.form_container {
  max-width: 400px;
}

.one_click_message {
  margin-left: 4px;
  font-size: 0.75rem;
  color: $text-muted;

  a {
    color: $primary;

    &:hover {
      color: $secondary;
    }
  }
}
</style>
